import { useMemo, useState } from 'react';
import salesforceTasksService from 'services/salesforceTasksService';
import useTaskFormOptions from './useTaskFormOptions';
import { validateSalesforceTaskDueDate } from '../components/Tasks/salesforceTaskValidation';
import { ButtonClickTargets } from 'constants/buttonClickTargets';

// Bulk actions available on the Work Queue table. Each descriptor declares the
// views it appears in, the bulk_update field/value it submits, and either a
// confirmation message (fixed value) or an input ('select'/'date') rendered in
// the confirmation modal.
const WORK_QUEUE_ACTIONS = [
	{
		key: 'mark-complete',
		label: 'Mark Complete',
		trackingTarget: ButtonClickTargets.WorkQueueMarkComplete,
		title: 'Mark tasks as complete',
		field: 'status',
		value: 'Completed',
		message: (count) =>
			`Are you sure you want to mark ${count} ${
				count === 1 ? 'task' : 'tasks'
			} as complete?`,
	},
	{
		key: 'reassign',
		label: 'Reassign',
		trackingTarget: ButtonClickTargets.WorkQueueReassign,
		title: 'Reassign tasks',
		field: 'owner_id',
		input: 'select',
		optionsKey: 'assignable_users',
		placeholder: 'Select an owner',
	},
	{
		key: 'update-status',
		label: 'Update Status',
		trackingTarget: ButtonClickTargets.WorkQueueUpdateStatus,
		title: 'Update task status',
		field: 'status',
		input: 'select',
		optionsKey: 'statuses',
		placeholder: 'Select a status',
	},
	{
		key: 'update-priority',
		label: 'Update Priority',
		trackingTarget: ButtonClickTargets.WorkQueueUpdatePriority,
		title: 'Update task priority',
		field: 'priority',
		input: 'select',
		optionsKey: 'priorities',
		placeholder: 'Select a priority',
	},
	{
		key: 'update-due-date',
		label: 'Update Due Date',
		trackingTarget: ButtonClickTargets.WorkQueueUpdateDueDate,
		title: 'Update task due date',
		field: 'due_date',
		input: 'date',
	},
	{
		key: 'reopen',
		label: 'Reopen Selected',
		trackingTarget: ButtonClickTargets.WorkQueueReopen,
		title: 'Reopen tasks',
		field: 'status',
		value: 'Not Started',
		// Only completed tasks can be reopened; others are skipped
		eligible: (task) => task.status === 'Completed',
		message: (count) =>
			`Are you sure you want to reopen ${count} ${
				count === 1 ? 'task' : 'tasks'
			}?`,
	},
];

// Owns the selection-action flow for the Work Queue table: which action is
// pending confirmation, the input value collected by its modal, and submitting
// the bulk update. Returns everything WorkQueueActionModal needs to render.
const useWorkQueueTableActions = ({
	tasks = [],
	refreshTasks,
	setRowSelectionModel,
	showNotification,
	trackButtonClick,
}) => {
	const [pendingAction, setPendingAction] = useState(null);
	const [actionValue, setActionValue] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const taskById = useMemo(
		() => new Map(tasks.map((task) => [task.id, task])),
		[tasks]
	);

	// Picklist options load lazily, only when an input action is opened
	const { formOptions, formOptionsLoading } = useTaskFormOptions(
		Boolean(pendingAction?.action?.optionsKey)
	);

	const actionButtons = useMemo(
		() =>
			WORK_QUEUE_ACTIONS.map((action) => ({
				key: action.key,
				label: action.label,
				onClickHandler: (selectedIds) => {
					trackButtonClick?.(action.trackingTarget);
					setActionValue(null);
					setPendingAction({ action, selectedIds });
				},
			})),
		[trackButtonClick]
	);

	const closeAction = () => {
		if (!submitting && pendingAction) {
			trackButtonClick?.(ButtonClickTargets.WorkQueueActionCancel);
			setPendingAction(null);
		}
	};

	const dueDateValidation =
		pendingAction?.action?.input === 'date'
			? validateSalesforceTaskDueDate(actionValue)
			: null;

	const confirmDisabled =
		submitting ||
		(pendingAction?.action?.input === 'select' && !actionValue) ||
		(pendingAction?.action?.input === 'date' &&
			!dueDateValidation?.isValid);

	// The value the action would write: fixed for confirm-only actions, the
	// picked option for selects, or the validated date for the date input
	const resolvedValue = pendingAction
		? pendingAction.action.input === 'date'
			? dueDateValidation?.formattedValue
			: (actionValue ?? pendingAction.action.value)
		: null;

	// Selected tasks that wouldn't change are skipped in the request and
	// reported as unchanged: the value is already set, or the action doesn't
	// apply to them (e.g. only completed tasks can be reopened)
	const unchangedIds = useMemo(() => {
		if (!pendingAction || resolvedValue == null) return [];

		const { field, eligible } = pendingAction.action;

		return pendingAction.selectedIds.filter((id) => {
			const task = taskById.get(id);
			if (!task) return false;

			return (
				task[field] === resolvedValue ||
				(eligible ? !eligible(task) : false)
			);
		});
	}, [pendingAction, resolvedValue, taskById]);

	const confirmAction = async () => {
		if (!pendingAction || confirmDisabled) return;

		trackButtonClick?.(ButtonClickTargets.WorkQueueActionConfirm);

		const { action, selectedIds } = pendingAction;
		const unchangedSet = new Set(unchangedIds);
		const changedIds = selectedIds.filter((id) => !unchangedSet.has(id));

		setSubmitting(true);

		try {
			// Nothing to send when every selected task is skipped
			if (changedIds.length === 0) {
				showNotification(
					selectedIds.length === 1
						? "No changes needed — the selected task won't be updated"
						: 'No changes needed — none of the selected tasks would be updated',
					'success'
				);

				setPendingAction(null);
				setRowSelectionModel?.([]);
				return;
			}

			const result = await salesforceTasksService.bulkUpdateTasks({
				taskIds: changedIds,
				field: action.field,
				value: resolvedValue,
			});

			const updated = result?.updated_count ?? 0;
			const failedIds = result?.failed_ids ?? [];
			const failed = failedIds.length;
			const unchanged = unchangedIds.length;
			const unchangedText =
				unchanged > 0 ? `; ${unchanged} unchanged` : '';

			showNotification(
				failed > 0
					? `Updated ${updated} ${updated === 1 ? 'task' : 'tasks'}; ${failed} failed${unchangedText}`
					: `Updated ${updated} ${updated === 1 ? 'task' : 'tasks'}${unchangedText}`,
				failed > 0 ? 'error' : 'success'
			);

			// Refresh first so the row set settles, then keep only the failed
			// tasks selected for retry/inspection
			setPendingAction(null);
			await refreshTasks?.();
			setRowSelectionModel?.(failedIds);
		} catch (error) {
			showNotification(
				error?.message ?? 'Unable to update tasks',
				'error'
			);
		} finally {
			setSubmitting(false);
		}
	};

	return {
		actionButtons,
		pendingAction,
		actionValue,
		setActionValue,
		submitting,
		confirmDisabled,
		formOptions,
		formOptionsLoading,
		closeAction,
		confirmAction,
		unchangedCount: unchangedIds.length,
	};
};

export default useWorkQueueTableActions;
